import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

const CATEGORIES_OUT = ['Salary','Salary Advance','TA (Travel Allowance)','Food Expenses','Marketing','Rent','Utilities','Office Expenses','Miscellaneous'];
const ENTRY_TYPES = [
  { key:'petty_cash',   label:'Petty Cash Expense', color:'#f59e0b' },
  { key:'site_expense', label:'Site Expense',        color:'#3b82f6' },
  { key:'reimbursement',label:'Reimbursement',       color:'#22c55e' },
];

const INR = v => `₹${Number(v||0).toLocaleString('en-IN')}`;

export default function StaffExpenses() {
  const { user } = useAuth();
  const [myStaff, setMyStaff] = useState(null);
  const [pettyCash, setPettyCash] = useState(null);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowedCategories, setAllowedCategories] = useState({});
  const [payModes, setPayModes] = useState([]);

  const emptyForm = {
    date: new Date().toISOString().split('T')[0],
    type: 'Cash Out',
    entry_type: 'site_expense',
    category: '',
    payment_mode: 'Cash',
    party_name: '',
    amount: '',
    reference: '',
    description: '',
    project_id: '',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => { if (user) loadMyData(); }, [user]);
  // approval / rejection of my entries and petty-cash balance changes show up at once
  useLiveRefresh(['expense_entries', 'petty_cash_accounts'], () => loadMyData(true), !!user);

  async function loadMyData(silent) {
    if (silent !== true) setLoading(true);
    // Get my staff record
    const { data: staff } = await supabase.from('staff')
      .select('*').eq('user_id', user.id).single();
    if (!staff) { setLoading(false); return; }
    setMyStaff(staff);

    // Get petty cash balance
    const { data: pc } = await supabase.from('petty_cash_accounts')
      .select('*').eq('staff_id', staff.id).maybeSingle();
    setPettyCash(pc);

    // Get my expense entries
    const { data: myEntries } = await supabase.from('expense_entries')
      .select('*').eq('staff_id', staff.id)
      .order('created_at', {ascending: false}).limit(50);
    setEntries(myEntries || []);

    // Get pay modes
    const { data: modes } = await supabase.from('accounts_payment_modes').select('name').order('name');
    setPayModes((modes||[]).map(m=>m.name));

    // Get projects
    const { data: proj } = await supabase.from('construction_projects')
      .select('id,project_name').eq('status','active').order('project_name');
    setProjects(proj || []);

    // Get allowed categories grouped by type
    const { data: cats } = await supabase.from('staff_expense_categories')
      .select('category').eq('staff_id', staff.id);
    if (cats?.length) {
      const allowed = cats.map(c=>c.category);
      const { data: allCats } = await supabase.from('accounts_categories')
        .select('name,category_type').in('name', allowed).order('name');
      const byType = {};
      (allCats||[]).forEach(c=>{ const t=c.category_type||'General'; if(!byType[t]) byType[t]=[]; byType[t].push(c.name); });
      setAllowedCategories(byType);
    } else {
      // No specific categories assigned — show General only
      const { data: allCats } = await supabase.from('accounts_categories')
        .select('name,category_type').eq('category_type','General').order('name');
      const byType = {'General': (allCats||[]).map(c=>c.name)};
      setAllowedCategories(byType);
    }

    setLoading(false);
  }

  async function submitEntry() {
    if (!form.category || !form.amount || !form.date) {
      toast.error('Date, category and amount are required');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('expense_entries').insert({
        ...form,
        staff_id: myStaff.id,
        amount: Number(form.amount),
        status: 'submitted',
        company: 'ARMSTECH',
      });
      if (error) throw error;
      toast.success('Expense submitted for review!');
      setShowForm(false);
      setForm(emptyForm);
      loadMyData();
    } catch(e) {
      toast.error(e.message);
    }
    setSaving(false);
  }

  const statusColors = {
    draft: '#6b7280', submitted: '#d97706',
    approved: '#16a34a', correction_needed: '#dc2626', rejected: '#dc2626'
  };
  const statusLabels = {
    draft: 'Draft', submitted: 'Pending Review',
    approved: 'Approved', correction_needed: 'Needs Correction', rejected: 'Rejected'
  };

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',fontSize:14,color:'#6b7280'}}>Loading...</div>;

  if (!myStaff) return (
    <div style={{padding:40,textAlign:'center',color:'#6b7280'}}>
      <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
      <h3>No staff record found for your account</h3>
      <p>Please contact admin to set up your staff profile.</p>
    </div>
  );

  return (
    <div style={{maxWidth:600,margin:'0 auto',padding:20,fontFamily:'sans-serif'}}>
      {/* Header */}
      <div style={{background:'linear-gradient(135deg,#00b09b,#96c93d)',borderRadius:12,padding:20,color:'#fff',marginBottom:20}}>
        <div style={{fontWeight:700,fontSize:18,marginBottom:4}}>Welcome, {myStaff.full_name}</div>
        <div style={{fontSize:13,opacity:0.9}}>{myStaff.role||'Staff'} · Armstech Engineers</div>
      </div>

      {/* Petty Cash Balance */}
      {pettyCash && (
        <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:16,marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12,color:'#374151'}}>💰 My Petty Cash</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,textAlign:'center'}}>
            <div style={{background:'#f0fdf4',borderRadius:8,padding:10}}>
              <div style={{fontWeight:800,fontSize:20,color:'#16a34a'}}>{INR(pettyCash.current_balance)}</div>
              <div style={{fontSize:11,color:'#6b7280',marginTop:2}}>Balance</div>
            </div>
            <div style={{background:'#eff6ff',borderRadius:8,padding:10}}>
              <div style={{fontWeight:800,fontSize:20,color:'#2563eb'}}>{INR(pettyCash.total_issued)}</div>
              <div style={{fontSize:11,color:'#6b7280',marginTop:2}}>Total Issued</div>
            </div>
            <div style={{background:'#fef2f2',borderRadius:8,padding:10}}>
              <div style={{fontWeight:800,fontSize:20,color:'#dc2626'}}>{INR(pettyCash.total_spent)}</div>
              <div style={{fontSize:11,color:'#6b7280',marginTop:2}}>Total Spent</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      <button onClick={()=>{setForm(emptyForm);setShowForm(true);}}
        style={{width:'100%',padding:'14px',background:'#00b09b',color:'#fff',border:'none',borderRadius:12,
          fontWeight:700,fontSize:15,cursor:'pointer',marginBottom:20,boxShadow:'0 4px 12px rgba(0,176,155,0.3)'}}>
        + Submit New Expense
      </button>

      {/* My Entries */}
      <div style={{fontWeight:700,fontSize:15,marginBottom:12,color:'#374151'}}>My Recent Entries</div>
      {entries.length === 0 && (
        <div style={{textAlign:'center',padding:40,color:'#9ca3af',fontSize:14}}>No expenses submitted yet</div>
      )}
      {entries.map(e => {
        const etype = ENTRY_TYPES.find(t=>t.key===e.entry_type);
        return (
          <div key={e.id} style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,padding:14,marginBottom:10,boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14,color:'#111827'}}>{e.category}</div>
                <div style={{fontSize:12,color:'#6b7280',marginTop:2}}>{fmtDMY(e.date)} · {e.payment_mode}</div>
                {e.description && <div style={{fontSize:12,color:'#374151',marginTop:4}}>{e.description}</div>}
                {e.rejection_reason && (
                  <div style={{fontSize:11,color:'#dc2626',marginTop:4,background:'#fef2f2',padding:'4px 8px',borderRadius:4}}>
                    ⚠️ {e.rejection_reason}
                  </div>
                )}
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontWeight:800,fontSize:16,color:e.type==='Cash Out'?'#dc2626':'#16a34a'}}>
                  {e.type==='Cash Out'?'-':'+'}{INR(e.amount)}
                </div>
                <div style={{fontSize:10,fontWeight:600,color:statusColors[e.status]||'#6b7280',
                  background:statusColors[e.status]+'15',padding:'2px 8px',borderRadius:4,marginTop:4}}>
                  {statusLabels[e.status]||e.status}
                </div>
                {etype && <div style={{fontSize:10,color:etype.color,marginTop:2}}>{etype.label}</div>}
              </div>
            </div>
          </div>
        );
      })}

      {/* Entry Form Modal */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}
          onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div style={{background:'#fff',borderRadius:'16px 16px 0 0',padding:20,width:'100%',maxWidth:600,maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <span style={{fontWeight:700,fontSize:16}}>New Expense Entry</span>
              <button onClick={()=>setShowForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#6b7280'}}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Entry Type *</label>
                <select value={form.entry_type} onChange={e=>sf('entry_type',e.target.value)}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14}}>
                  {ENTRY_TYPES.map(t=><option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Date *</label>
                <DateInput value={form.date} onChange={e=>sf('date',e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Category *</label>
                <select value={form.category} onChange={e=>sf('category',e.target.value)}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14}}>
                  <option value="">Select Category</option>
                  {Object.keys(allowedCategories).sort().map(type=>(
                    <optgroup key={type} label={type}>
                      {allowedCategories[type].map(cat=><option key={cat} value={cat}>{cat}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Payment Mode</label>
                <select value={form.payment_mode} onChange={e=>sf('payment_mode',e.target.value)}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14}}>
                  {payModes.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Amount (₹) *</label>
                <input type="number" value={form.amount} onChange={e=>sf('amount',e.target.value)}
                  placeholder="0.00" min={0}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Party Name</label>
                <input value={form.party_name} onChange={e=>sf('party_name',e.target.value)}
                  placeholder="Vendor / Party name"
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Reference / Receipt No</label>
                <input value={form.reference} onChange={e=>sf('reference',e.target.value)}
                  placeholder="Receipt number"
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box'}}/>
              </div>
              {projects.length > 0 && (
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Project (if applicable)</label>
                  <select value={form.project_id} onChange={e=>sf('project_id',e.target.value)}
                    style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14}}>
                    <option value="">No Project</option>
                    {projects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:4}}>Description</label>
                <textarea value={form.description} onChange={e=>sf('description',e.target.value)}
                  placeholder="Details of this expense..." rows={3}
                  style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box',resize:'vertical'}}/>
              </div>
              <button onClick={submitEntry} disabled={saving}
                style={{padding:'14px',background:saving?'#9ca3af':'#00b09b',color:'#fff',border:'none',
                  borderRadius:10,fontWeight:700,fontSize:15,cursor:saving?'not-allowed':'pointer',marginTop:4}}>
                {saving?'Submitting...':'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
